// frontend/src/pages/auth/VerifyEmailPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CssBaseline,
  CircularProgress,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';

// Import your logo
import syncroLogo from '../../assets/syncro_logo_color.svg';

const GradientBackground = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f0f4f8 0%, #e6ebee 100%)',
  padding: 16,
});

const StyledPaper = styled(Paper)({
  padding: 32,
  borderRadius: 20,
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 12px 32px rgba(31, 38, 135, 0.12)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.7)',
});

const VerifyEmailPage = () => {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { id, hash } = useParams();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!id || !hash) {
        setError('Invalid verification link');
        setLoading(false);
        return;
      }

      try {
        await authAPI.verifyEmail({ id, hash });
        setSuccess(true);
        setTimeout(() => navigate('/login?verified=1'), 3000);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to verify email');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [navigate, id, hash]);

  return (
    <GradientBackground>
      <CssBaseline />
      <StyledPaper elevation={0}>
        <Fade in={true} timeout={500}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src={syncroLogo}
              alt="Syncro Logo"
              sx={{
                height: 72,
                objectFit: 'contain'
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Verify Your Email
            </Typography>
          </Box>
        </Fade>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={60} sx={{ color: '#4361ee' }} />
              <Typography variant="h6" color="text.secondary">
                Verifying your email...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                Please wait while we confirm your email address
              </Typography>
            </Box>
          ) : success ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <SuccessIcon color="success" sx={{ fontSize: 60 }} />
              <Typography variant="h6" color="success.main" fontWeight="bold">
                Verification Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                Your email has been verified. Redirecting to login...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <ErrorIcon color="error" sx={{ fontSize: 60 }} />
              <Typography variant="h6" color="error.main" fontWeight="bold">
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
                {error}
              </Alert>
            </Box>
          )}
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{
            mt: 2,
            py: 1.2,
            background: 'linear-gradient(135deg, #4361ee, #3a0ca3)',
            color: 'white',
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: '16px',
            borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(67, 97, 238, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3a50e2, #2d0a9a)',
              boxShadow: '0 6px 20px rgba(67, 97, 238, 0.4)',
            }
          }}
        >
          Go to Login
        </Button>

        {/* Footer */}
        <Box sx={{ 
          textAlign: 'center', 
          mt: 4, 
          pt: 2, 
          borderTop: '1px solid #e2e8f0',
          color: '#718096',
          fontSize: '0.8rem'
        }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Syncro. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Designed & Developed by{' '}
            <a
              href="https://www.softsolutionmedia.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4361ee', textDecoration: 'none' }}
            >
              Soft Solution Media
            </a>
          </Typography>
        </Box>
      </StyledPaper>
    </GradientBackground>
  );
};

export default VerifyEmailPage;